import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Spinner } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { googleBusinessApi, googleBusinessKeys } from "./googleBusinessApi";
import { GoogleBusinessLocationDetail } from "./GoogleBusinessLocationDetail";

/**
 * "Connect Google Business" card on the Channels→Google tab (tasks/google-business-connect, Phase 1).
 * Connecting is a full-page redirect to Google, so the callback returns here with
 * `?google_business=connected|error|denied|invalid_state`; on `connected` we sync locations once and
 * clear the marker. No Google token ever reaches the SPA — this only shows status + mapping metadata.
 */
export function GoogleBusinessCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const returned = params.get("google_business");
  const [openLocationId, setOpenLocationId] = useState<string | null>(null);

  const status = useQuery({
    queryKey: googleBusinessKeys.status(),
    queryFn: () => googleBusinessApi.getStatus(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: googleBusinessKeys.status() });

  const connect = useMutation({
    mutationFn: () => googleBusinessApi.connect(),
    onSuccess: ({ consentUrl }) => {
      if (consentUrl) {
        // The OAuth callback returns to the app root (the configured return URL); stash where we
        // are so RootRedirect can bring us back to this tab with the ?google_business result.
        sessionStorage.setItem("gb_return", window.location.pathname + window.location.search);
        window.location.assign(consentUrl);
      }
    },
  });

  const sync = useMutation({
    mutationFn: () => googleBusinessApi.sync(),
    onSuccess: invalidate,
  });

  const disconnect = useMutation({
    mutationFn: () => googleBusinessApi.disconnect(),
    onSuccess: invalidate,
  });

  // On a successful callback return, sync once (auto-map the just-connected locations) and drop the
  // marker so a refresh doesn't repeat it. Guarded by a ref because setParams re-renders.
  const handledReturn = useRef(false);
  useEffect(() => {
    if (returned && !handledReturn.current) {
      handledReturn.current = true;
      if (returned === "connected") sync.mutate();
      const next = new URLSearchParams(params);
      next.delete("google_business");
      setParams(next, { replace: true });
    }
  }, [returned, params, setParams, sync]);

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "4", mt: "6" })}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <h3 className={css({ fontSize: "md", fontWeight: "semibold", color: "fg.default" })}>
          {t("googleBusiness.title")}
        </h3>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("googleBusiness.intro")}</p>
      </div>

      {returned && returned !== "connected" && (
        <Alert.Root>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>{t(`googleBusiness.return.${returned}`)}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}

      {status.isLoading && <Spinner aria-label={t("googleBusiness.loading")} />}
      {status.isError && (
        <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
          {t("googleBusiness.loadError")}
        </p>
      )}

      {status.data && !status.data.connected && (
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "3",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "lg",
            p: "4",
          })}
        >
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>
            {t("googleBusiness.notConnected")}
          </p>
          <Button size="sm" loading={connect.isPending} onClick={() => connect.mutate()}>
            {t("googleBusiness.connect")}
          </Button>
          {connect.isError && (
            <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
              {t("googleBusiness.connectError")}
            </p>
          )}
        </div>
      )}

      {status.data?.connected && (
        <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
          <div
            className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}
          >
            <Badge colorPalette="green">{t("googleBusiness.connected")}</Badge>
            {status.data.googleAccountId && (
              <span className={css({ fontSize: "xs", color: "fg.muted" })}>
                {status.data.googleAccountId}
              </span>
            )}
          </div>

          {status.data.locations.length === 0 ? (
            <p className={css({ fontSize: "sm", color: "fg.muted" })}>
              {t("googleBusiness.noLocations")}
            </p>
          ) : (
            <ul className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
              {status.data.locations.map((loc) => {
                const open = openLocationId === loc.locationId;
                return (
                  <li
                    key={loc.locationId}
                    className={css({
                      display: "flex",
                      flexDirection: "column",
                      borderWidth: "1px",
                      borderColor: "border.default",
                      rounded: "md",
                      px: "3",
                      py: "2",
                    })}
                  >
                    <div
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "3",
                      })}
                    >
                      <span className={css({ fontSize: "sm", color: "fg.default" })}>
                        {loc.title}
                      </span>
                      <div className={css({ display: "flex", alignItems: "center", gap: "2" })}>
                        <Badge colorPalette={loc.venueId ? "brand" : "gray"}>
                          {loc.venueId ? t("googleBusiness.mapped") : t("googleBusiness.unmapped")}
                        </Badge>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setOpenLocationId(open ? null : loc.locationId)}
                        >
                          {open
                            ? t("googleBusiness.details.hide")
                            : t("googleBusiness.details.show")}
                        </Button>
                      </div>
                    </div>
                    {open && <GoogleBusinessLocationDetail locationId={loc.locationId} />}
                  </li>
                );
              })}
            </ul>
          )}

          <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
            <Button
              size="sm"
              variant="outline"
              loading={sync.isPending}
              onClick={() => sync.mutate()}
            >
              {t("googleBusiness.sync")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              loading={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              {t("googleBusiness.disconnect")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
